import {
  LoginFlow,
  RecoveryFlow,
  RegistrationFlow,
  SettingsFlow,
} from '@ory/client'
import {
  EmbeddedCredentialMode,
  generateRandomSlug,
} from '@web-apps/utils-shared'
import { useCallback, useEffect, useRef, useState } from 'react'

import * as Sentry from '@sentry/browser'
import { useFlag } from '@unleash/proxy-client-react'
import { useLogoutMutation, useValidateSlugMutation } from '../api'
import { useCheckSessionMutation } from '../api/auth.api'
import { configureSentryErrors } from './errors'
import { FLAG_SEAMLESS_AUTH } from './flags.constants'
import {
  captureSeamlessAuthMarker,
  hasSeamlessAuthMarker,
  ResolvedSession,
  resolveSession,
  SessionStatus,
} from './session'
import {
  getUserAuthSession,
  initiateLogoutFlow,
  initiateRecoverFlow,
  initiateSettingsFlow,
} from './flows'
import { isUiNodeInputAttributes } from './helpers'
import {
  LoginRegisterRecoverFieldsFormatTypes,
  NodeInputType,
  OryFormInputTypes,
  ProviderTypes,
  SessionType,
  SettingsFieldsFormatTypes,
} from './types'

// TODO: figure out how to check session on public app with seamless login logic
export const useGetSessionForGetStartedPage = (
  reloadSession?: boolean
): {
  isSessionLoading: boolean
  session: SessionType | null
} => {
  const [isLoading, setIsLoading] = useState(true)
  const [session, setSession] = useState<SessionType | null>(null)

  const getSession = useCallback(async () => {
    try {
      const session = await getUserAuthSession()
      setSession(
        session
          ? { ...(session as unknown as SessionType), source: 'ory' }
          : null
      )
      setIsLoading(false)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      // 401 is always returned when a user is not logged in, any other code is an error
      if (error.response?.status !== 401 && error.status !== 401) {
        Sentry.withScope(function (scope) {
          scope
            .setTag('operations', 'ory')
            .setLevel('error')
            .setFingerprint(['getUserAuthSession'])
            .setExtras({
              action: 'getUserAuthSession',
              ...configureSentryErrors(error),
            })
          Sentry.captureException(error)
        })
      }
      setSession(null)
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    if (isLoading) getSession()
  }, [getSession, isLoading, reloadSession])

  useEffect(() => {
    if (reloadSession) getSession()
  }, [getSession, reloadSession])

  return {
    isSessionLoading: isLoading,
    session,
  }
}

type UseGetSessionParams = {
  isEmbedded?: boolean
  hasEmbeddedCredential?: boolean
  embeddedCredentialMode?: EmbeddedCredentialMode | null
  impersonatedUserId?: string | null
  resolveKey?: number
}

export type UseGetSessionResult = Omit<ResolvedSession, 'status'> & {
  isSessionLoading: boolean
  status: SessionStatus | 'loading'
}

export const useGetSession = ({
  isEmbedded = false,
  hasEmbeddedCredential = false,
  embeddedCredentialMode = null,
  impersonatedUserId = null,
  resolveKey = 0,
}: UseGetSessionParams = {}): UseGetSessionResult => {
  const isSeamlessAuthEnabled = useFlag(FLAG_SEAMLESS_AUTH)
  const [checkZezamSession] = useCheckSessionMutation()
  const [isLoading, setIsLoading] = useState(true)
  const [resolved, setResolved] = useState<ResolvedSession>({
    status: 'anonymous',
    session: null,
  })

  const isRequestInFlightRef = useRef(false)

  const fetchZezamSession = useCallback(
    () => checkZezamSession().unwrap(),
    [checkZezamSession]
  )

  const getSession = useCallback(async () => {
    if (isRequestInFlightRef.current) return

    isRequestInFlightRef.current = true
    setIsLoading(true)

    captureSeamlessAuthMarker()

    try {
      const result = await resolveSession({
        isEmbedded,
        hasEmbeddedCredential,
        embeddedCredentialMode,
        isSeamlessAuth:
          !isEmbedded && isSeamlessAuthEnabled && hasSeamlessAuthMarker(),
        impersonatedUserId,
        fetchZezamSession,
      })

      setResolved(result)
    } finally {
      isRequestInFlightRef.current = false
      setIsLoading(false)
    }
  }, [
    isEmbedded,
    hasEmbeddedCredential,
    embeddedCredentialMode,
    isSeamlessAuthEnabled,
    impersonatedUserId,
    fetchZezamSession,
  ])

  useEffect(() => {
    getSession()
  }, [getSession, resolveKey])

  return {
    ...resolved,
    status: isLoading ? 'loading' : resolved.status,
    isSessionLoading: isLoading,
  }
}

/*
 *
 * This method is used for login, registration & reset-password flows
 *
 * If it is a 0auth provider (Facebook, Google, etc.), we need to have a separate
 * form that handles sending only provider name and let it handle further by
 * this provider.
 *
 * If it is a classic email+password flow, we need a separate form with the
 * needed inputs and a separate dataset.
 *
 * Fields:
 *
 * 1. `csrf_token` is important for any transaction and should always be added to
 * the form submission.
 *
 * 2. `identifier` || `traits.email` || `email` is an email.
 *
 * 3. `provider` is a 0auth services (Github, Facebook, etc.)
 *
 * 4. `method` is a type needed for email login, we should only include it for email.
 *
 * 5. `password` is a password field, we don't really need to render it from the
 * fields array, but we still use it to render our custom field.
 *
 * */

export const useFormatLoginRegisterRecoveryFlowNodes = (
  flow: LoginFlow | RegistrationFlow | RecoveryFlow | null
): { fields: LoginRegisterRecoverFieldsFormatTypes } => {
  const [fields, setFields] = useState<LoginRegisterRecoverFieldsFormatTypes>({
    csrfToken: [],
    google: [],
    github: [],
    facebook: [],
    twitter: [],
    apple: [],
    tiktok: [],
    email: [],
    password: [],
    method: [],
  })

  useEffect(() => {
    const fields = flow?.ui?.nodes as NodeInputType[]

    if (fields) {
      const tokenFields = fields.filter(
        ({ attributes }) => attributes.name === OryFormInputTypes.CSRF
      )

      const methodFields = fields.filter(
        ({ attributes }) => attributes.name === OryFormInputTypes.METHOD
      )

      const emailFields = fields.filter(
        ({ attributes }) =>
          attributes.name === OryFormInputTypes.IDENTIFIER ||
          attributes.name === OryFormInputTypes.TRAITS_EMAIL ||
          attributes.name === OryFormInputTypes.EMAIL
      )

      const passwordFields = fields.filter(
        ({ attributes }) => attributes.name === OryFormInputTypes.PASSWORD
      )

      const googleFields = fields.filter(
        ({ attributes }) =>
          attributes.name === OryFormInputTypes.PROVIDER &&
          attributes.value.startsWith(ProviderTypes.GOOGLE)
      )

      const githubFields = fields.filter(
        ({ attributes }) =>
          attributes.name === OryFormInputTypes.PROVIDER &&
          attributes.value.startsWith(ProviderTypes.GITHUB)
      )

      const facebookFields = fields.filter(
        ({ attributes }) =>
          attributes.name === OryFormInputTypes.PROVIDER &&
          attributes.value.startsWith(ProviderTypes.FACEBOOK)
      )

      const twitterFields = fields.filter(
        ({ attributes }) =>
          attributes.name === OryFormInputTypes.PROVIDER &&
          attributes.value.startsWith(ProviderTypes.TWITTER)
      )

      const appleFields = fields.filter(
        ({ attributes }) =>
          attributes.name === OryFormInputTypes.PROVIDER &&
          attributes.value.startsWith(ProviderTypes.APPLE)
      )

      const tiktokFields = fields.filter(
        ({ attributes }) =>
          attributes.name === OryFormInputTypes.PROVIDER &&
          attributes.value === ProviderTypes.TIKTOK
      )

      setFields({
        csrfToken: tokenFields,
        method: methodFields,
        email: emailFields,
        password: passwordFields,
        google: googleFields,
        github: githubFields,
        facebook: facebookFields,
        twitter: twitterFields,
        apple: appleFields,
        tiktok: tiktokFields,
      })
    }
  }, [flow?.ui?.nodes])

  return {
    fields,
  }
}

/*
 *
 * This method is used for settings flow.
 *
 * For every 0auth provider (Facebook, Google, etc.), we need to have a separate
 * form that handles sending only provider name and let it handle further by
 * this provider.
 *
 * For a classic email+password flow, we need a separate form with the
 * needed inputs and a separate dataset.
 *
 * Fields:
 *
 * 1. `csrf_token` is important for any transaction and should always be added to
 * the form submission.
 *
 * 2. `link` is used for adding 0auth services (Github, Facebook, etc.)
 *
 * 3. `method` is a type with value `password` needed for email login, we should only include it for email.
 *
 * 4. `password` is a password field, we don't really need to render it from the
 * fields array, but we still use it to render our custom field.
 *
 * */

export const useFormatSettingsFlowNodes = (
  flow: SettingsFlow | null
): { fields: SettingsFieldsFormatTypes } => {
  const [fields, setFields] = useState<SettingsFieldsFormatTypes>({
    csrfToken: [],
    google: [],
    github: [],
    facebook: [],
    twitter: [],
    apple: [],
    tiktok: [],
    password: [],
    method: [],
  })

  useEffect(() => {
    const fields = flow?.ui?.nodes as NodeInputType[]

    if (fields) {
      // Adding placeholder to the fields
      const formattedFields = fields
        ?.filter(({ attributes }) => isUiNodeInputAttributes(attributes))
        .map((field) => {
          switch (field.attributes.name) {
            case OryFormInputTypes.PASSWORD:
              return {
                ...field,
                attributes: { ...field.attributes, placeholder: 'Password' },
              }
            default:
              return field
          }
        })

      const tokenFields = formattedFields.filter(
        ({ attributes }) => attributes.name === OryFormInputTypes.CSRF
      )

      const methodFields = formattedFields.filter(
        ({ attributes }) =>
          attributes.name === OryFormInputTypes.METHOD &&
          attributes.value === 'password'
      )

      const passwordFields = formattedFields.filter(
        ({ attributes }) => attributes.name === OryFormInputTypes.PASSWORD
      )

      const googleFields = formattedFields.filter(
        ({ attributes }) =>
          (attributes.name === OryFormInputTypes.LINK ||
            attributes.name === OryFormInputTypes.UNLINK) &&
          attributes.value === ProviderTypes.GOOGLE
      )

      const githubFields = formattedFields.filter(
        ({ attributes }) =>
          (attributes.name === OryFormInputTypes.LINK ||
            attributes.name === OryFormInputTypes.UNLINK) &&
          attributes.value === ProviderTypes.GITHUB
      )

      const facebookFields = formattedFields.filter(
        ({ attributes }) =>
          (attributes.name === OryFormInputTypes.LINK ||
            attributes.name === OryFormInputTypes.UNLINK) &&
          attributes.value === ProviderTypes.FACEBOOK
      )

      const twitterFields = formattedFields.filter(
        ({ attributes }) =>
          (attributes.name === OryFormInputTypes.LINK ||
            attributes.name === OryFormInputTypes.UNLINK) &&
          attributes.value === ProviderTypes.TWITTER
      )

      const appleFields = formattedFields.filter(
        ({ attributes }) =>
          (attributes.name === OryFormInputTypes.LINK ||
            attributes.name === OryFormInputTypes.UNLINK) &&
          attributes.value === ProviderTypes.APPLE
      )

      const tiktokFields = formattedFields.filter(
        ({ attributes }) =>
          (attributes.name === OryFormInputTypes.LINK ||
            attributes.name === OryFormInputTypes.UNLINK) &&
          attributes.value === ProviderTypes.TIKTOK
      )

      setFields({
        csrfToken: tokenFields,
        method: methodFields,
        password: passwordFields,
        google: googleFields,
        github: githubFields,
        facebook: facebookFields,
        twitter: twitterFields,
        apple: appleFields,
        tiktok: tiktokFields,
      })
    }
  }, [flow?.ui?.nodes])

  return {
    fields,
  }
}

export const useGetRecoveryFlow = (): {
  flow: RecoveryFlow | null
  isFlowLoading: boolean
  fields: LoginRegisterRecoverFieldsFormatTypes
  error?: string
} => {
  const [flow, setFlow] = useState<RecoveryFlow | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string>('')
  const { fields } = useFormatLoginRegisterRecoveryFlowNodes(flow)

  useEffect(() => {
    const createFlow = async () => {
      setIsLoading(true)
      const flowResponse = await initiateRecoverFlow()

      if (flowResponse.error) {
        setError(flowResponse.error)
      } else if (flowResponse.flow) {
        setFlow(flowResponse.flow)
      }
      setIsLoading(false)
    }

    createFlow()
  }, [])

  return {
    isFlowLoading: isLoading,
    flow,
    fields,
    error,
  }
}

export const useCreateLogoutFlow = (): {
  url?: string
  token?: string
  isLoading: boolean
  error?: {
    message?: string
    status: number
  }
} => {
  const [seamlessLogout] = useLogoutMutation()
  const { session, isSessionLoading } = useGetSession()
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<
    | {
        message?: string
        status: number
      }
    | undefined
  >(undefined)
  const [logoutData, setLogoutData] = useState<
    | {
        url?: string
        token?: string
        error?: {
          message?: string
          status: number
        }
      }
    | undefined
  >()

  useEffect(() => {
    const getLogoutLink = async () => {
      await seamlessLogout()
      const logoutData = await initiateLogoutFlow()
      if (logoutData.error) {
        setError(logoutData.error)
      } else {
        setLogoutData(logoutData)
      }
    }

    if (session) {
      getLogoutLink()
    } else {
      if (!isSessionLoading) setIsLoading(false)
    }
  }, [session, isSessionLoading, seamlessLogout])

  return {
    url: logoutData?.url,
    token: logoutData?.token,
    isLoading,
    error,
  }
}

export const useGetSettingsFlow = () => {
  const [flow, setFlow] = useState<SettingsFlow | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string>('')
  const { fields } = useFormatSettingsFlowNodes(flow)

  useEffect(() => {
    const createFlow = async () => {
      setIsLoading(true)
      const flowResponse = await initiateSettingsFlow()

      if (flowResponse.error) {
        setError(flowResponse.error)
      } else if (flowResponse.flow) {
        setFlow(flowResponse.flow)
      }
      setIsLoading(false)
    }

    createFlow()
  }, [])

  return { isFlowLoading: isLoading, error, flow, fields }
}

type UseRandomSlugProps = {
  skip?: boolean
  maxRetryAttempts?: number
  baseURL: string
}

type UseRandomSlugResult = {
  validSlug?: string
  isGeneratingSlug: boolean
}

/**
 * Hook responsible for
 *  1. generate a random slug
 *  2. validate it with server
 *  3. when not valid retry to a max of retries defined
 *
 * @param props
 *  skip (optional) - true if we want to skip it
 *    default: false
 *  maxRetryAttempts (optional) - number of retries if validation of slug fails
 *    default: 5
 *
 * @returns
 *  validSlug: undefined when not possible to validate it
 * isGeneratingSlug: true when it is generating and validating slug
 */
export const useRandomSlug = (
  props: UseRandomSlugProps
): UseRandomSlugResult => {
  const skip = props?.skip || false
  const maxRetryAttempts =
    typeof props.maxRetryAttempts === 'number' && props.maxRetryAttempts >= 0
      ? props.maxRetryAttempts
      : 5

  const attemptsCountRef = useRef(0)
  const [isGeneratingSlug, setGeneratingSlug] = useState<boolean>(false)
  const [validSlug, setValidSlug] = useState<string>()
  const [validateSlug] = useValidateSlugMutation()

  useEffect(() => {
    const generateAndValidateSlug = async () => {
      if (skip) return

      setGeneratingSlug(true)
      const slug = generateRandomSlug()
      const validateSlugResponse = await validateSlug({ slug })

      if ('error' in validateSlugResponse) {
        if (attemptsCountRef.current < maxRetryAttempts) {
          attemptsCountRef.current++
          setGeneratingSlug(false)
          generateAndValidateSlug()
        }
      } else {
        setGeneratingSlug(false)
        setValidSlug(slug)
      }
    }

    if (!skip) {
      generateAndValidateSlug()
    }
  }, [skip, maxRetryAttempts, props.baseURL, validateSlug])

  return {
    validSlug,
    isGeneratingSlug:
      isGeneratingSlug && attemptsCountRef.current < maxRetryAttempts,
  }
}
